/** external */
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import {
  UploadOutlined,
  OneDriveColored,
  GoogleDriveColored,
  DeleteOutlined,
  GoogleFormBrand,
  LinkOutlined,
} from "@toddle-edu/ds-icons";
import client from "lib/apolloClient/client";
import { compose } from "lib/apolloClient";
import { IconButton, MediaCard, Tooltip } from "@toddle-edu/ds-web";

/** internal */
import ACLStore from "lib/aclStore";
import GooglePicker from "UIComponents/GooglePicker";
import MicrosoftPicker from "UIComponents/MicrosoftPicker";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import LinkModal from "UIComponents/LinkModal";
import { ALLOWED_FILE_TYPES_V2 } from "AppComponents/AIContextBar/modules/Constants";
import { createAttachmentsMutation } from "Worksheet/modules/WorksheetMutations";
import { deleteAIConversationAttachment } from "Worksheet/modules/WorksheetModules";
import { useI18n } from "Hooks";
import { MAX_SIZE_10_MB } from "Worksheet/modules/WorksheetConstants";
import TheatreModeV2 from "UIComponents/TheatreModeV2/TheatreModeV2";
import { ENABLE_GOOGLE_DRIVE } from "Constants/permissionConstants";

/** relative */
import classes from "./FileAndFormPicker.scss";

const URL_MIME_TYPE = "text/uri-list";

const FileAndFormPicker = props => {
  const {
    parentId,
    parentType,
    portalType,
    isToddleLibraryPortal,
    attachments,
    setAttachments,
    deleteAIConversationAttachment,
    uploadingFile,
    setIsUploadingFile,
    conversationAttachments,
  } = props;

  const isGoogleDriveEnabled = ACLStore.can(ENABLE_GOOGLE_DRIVE);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [file, setFile] = useState(null);
  const [hasAttachment, setHasAttachments] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const { t } = useI18n(["common", "worksheet"]);

  useEffect(() => {
    if (!_.isEmpty(attachments) && !file) {
      setFile(_.first(attachments));
    } else if (!_.isEmpty(conversationAttachments) && !file) {
      setFile(_.first(conversationAttachments));
    } else if (_.isEmpty(conversationAttachments) && _.isEmpty(attachments)) {
      setFile(null);
    }
  }, [attachments, conversationAttachments]);

  useEffect(() => {
    setHasAttachments(!!file || uploadingFile);
  }, [file, uploadingFile]);

  // * Uploading file and creating attachment
  const onUpload = async file => {
    if (_.isEmpty(file) || uploadingFile) return;
    setIsUploadingFile(file);
    try {
      if (hasAttachment) {
        // * Removing attachment before creating new one, since only one is allowed
        deleteAIConversationAttachment({
          aiConversationId: parentId,
          input: { attachmentIds: [_.get(attachments, "0.id")] },
          ...(isToddleLibraryPortal ? { portalType: "PLANNER" } : {}),
        });
        setAttachments([]);
      }

      // * Need this in Backend for detecting of link
      if (_.get(file, "type") === "LINK") {
        _.set(file, "mimeType", URL_MIME_TYPE);
      }

      // * Creating attachment
      const response = await client.mutate({
        mutation: createAttachmentsMutation,
        variables: {
          input: [
            {
              ...file,
              parentId: parentId,
              parentType: "AI_CONVERSATION",
            },
          ],
        },
        // Library route defaults auth to COMMUNITY (oid: "-7"); force PLANNER to use teacher's real org token.
        context:
          portalType === "PLANNER" ? { portalType: "PLANNER" } : undefined,
      });
      const attachmentFile = _.get(
        response,
        "data.platform.createAttachment.0"
      );
      if (attachmentFile) setAttachments([attachmentFile]);
    } catch (error) {}
    setIsUploadingFile(false);
  };

  // * Removing attachment
  const onRemove = attachmentId => {
    if (!hasAttachment) return;
    deleteAIConversationAttachment({
      aiConversationId: parentId,
      input: { attachmentIds: [attachmentId] },
      ...(isToddleLibraryPortal ? { portalType: "PLANNER" } : {}),
    });
    setAttachments([]);
  };

  const renderComponentClassname = classNames({
    [classes.renderComponent]: !hasAttachment,
    [classes.renderComponentWithAttachment]: hasAttachment,
  });

  const uploadButtonContainerClasses = classNames({
    [classes.uploadButtonContainer]: true,
    [classes.justifyContentUnset]: hasAttachment || uploadingFile,
  });

  const supportedQuestionsTypes = [
    t("common:text_response"),
    t("worksheet:mcq"),
    t("common:fill_in_the_blanks"),
    t("common:true_or_false"),
    t("worksheet:multipart"),
  ];

  const supportedFormatTextClasses = classNames(
    "text-body-s",
    classes.supportedFormatText
  );

  let mimeType = _.get(uploadingFile || file, "mimeType");
  if (mimeType === URL_MIME_TYPE) mimeType = null;

  return (
    <div className={classes.filePickerContainer}>
      <DropzoneComponent
        multiple={false}
        accept={ALLOWED_FILE_TYPES_V2}
        showConfirmationDialogue={true}
        dropZoneStyle={{ cursor: "default" }}
        onSingleContentUploaded={onUpload}
        updateFilesCount={() => {}}
        disableDragNDrop={hasAttachment || uploadingFile}
        testId="worksheet-ai-media-reference-dropzone"
        maxSize={MAX_SIZE_10_MB}
        portalType={portalType}
        renderComponent={
          <div className={renderComponentClassname}>
            {!hasAttachment ? (
              <>
                <div className="text-label">
                  {t("worksheet:drag_drop_or_choose_action")}
                </div>
                <div className={supportedFormatTextClasses}>
                  <span>
                    {t("common:supported_formats", {
                      formats: ".docs, .pdf, .xls, .zip(QTI)",
                    })}
                  </span>
                  <span>{`${t(
                    "worksheet:supported_question_types"
                  )}: ${supportedQuestionsTypes.join(", ")}`}</span>
                  <span>{`${t("worksheet:extraction_size_page_limit")} (${t(
                    "worksheet:picture_extraction_not_supported"
                  )})`}</span>
                </div>
              </>
            ) : (
              <div>
                <MediaCard
                  dsVersion="2.0"
                  cardType="medium"
                  mimeType={mimeType}
                  disabled={uploadingFile}
                  url={_.get(uploadingFile || file, "url")}
                  testId="worksheet-ai-media-reference-media-card"
                  type={_.get(uploadingFile || file, "type") || "FILE"}
                  size={uploadingFile ? null : _.get(file, "metadata.size", 0)}
                  onClick={e => {
                    e?.stopPropagation();
                    setShowMediaModal(true);
                  }}
                  name={
                    uploadingFile
                      ? t("worksheet:adding_file")
                      : _.get(file, "name") || t("common:untitled")
                  }
                  options={
                    uploadingFile ? null : (
                      <IconButton
                        dsVersion="2.0"
                        onClick={() => onRemove(_.get(file, "id"))}
                        icon={
                          <DeleteOutlined variant="subtle" size="xx-small" />
                        }
                        variant="neutral"
                        shouldStopPropagation
                        testId={`worksheet-ai-media-reference-media-card:delete`}
                      />
                    )
                  }
                />
              </div>
            )}
            <div className={uploadButtonContainerClasses}>
              <div>
                <DropzoneComponent
                  multiple={false}
                  accept={ALLOWED_FILE_TYPES_V2}
                  updateFilesCount={() => {}}
                  showConfirmationDialogue={true}
                  onSingleContentUploaded={onUpload}
                  maxSize={MAX_SIZE_10_MB}
                  portalType={portalType}
                  renderComponent={
                    <Tooltip
                      dsVersion="2.0"
                      tooltip={t("common:upload_device")}
                      showArrow={true}
                      placement="bottom"
                    >
                      <IconButton
                        dsVersion="2.0"
                        variant="neutral"
                        type="outlined"
                        size="small"
                        icon={<UploadOutlined />}
                        tooltip={t("common:upload_device")}
                        testId="worksheet-extract-from-doc-upload-button"
                      />
                    </Tooltip>
                  }
                />
              </div>

              {isGoogleDriveEnabled && (
                <GooglePicker
                  maxSize={MAX_SIZE_10_MB}
                  parentId={parentId}
                  parentType={parentType}
                  multiselect={false}
                  shouldUpload={true}
                  exportAsPdf={true}
                  enableShareModal={false}
                  pickerType="GoogleDrive"
                  accept={ALLOWED_FILE_TYPES_V2}
                  updateFilesCount={() => {}}
                  onSingleContentUploaded={onUpload}
                  portalType={portalType}
                  renderComponent={
                    <Tooltip
                      dsVersion="2.0"
                      tooltip={t("common:google_drive")}
                      showArrow={true}
                      placement="bottom"
                    >
                      <IconButton
                        dsVersion="2.0"
                        variant="neutral"
                        type="outlined"
                        size="small"
                        icon={<GoogleDriveColored />}
                        tooltip={t("common:google_drive")}
                        testId="worksheet-extract-from-doc-google-drive-button"
                      />
                    </Tooltip>
                  }
                />
              )}

              {isGoogleDriveEnabled && (
                <MicrosoftPicker
                  maxSize={MAX_SIZE_10_MB}
                  exportAsPdf={true}
                  multiselect={false}
                  parentId={parentId}
                  parentType={parentType}
                  pickerType="GoogleDrive"
                  enableShareModal={false}
                  accept={ALLOWED_FILE_TYPES_V2}
                  onSingleContentUploaded={onUpload}
                  updateFilesCount={() => {}}
                  portalType={portalType}
                  renderComponent={
                    <Tooltip
                      dsVersion="2.0"
                      tooltip={t("common:one_drive_with_space")}
                      showArrow={true}
                      placement="bottom"
                    >
                      <IconButton
                        dsVersion="2.0"
                        variant="neutral"
                        type="outlined"
                        size="small"
                        icon={<OneDriveColored />}
                        tooltip={t("common:one_drive_with_space")}
                        testId="worksheet-extract-from-doc-one-drive-button"
                      />
                    </Tooltip>
                  }
                />
              )}

              {isGoogleDriveEnabled && (
                <GooglePicker
                  maxSize={MAX_SIZE_10_MB}
                  parentId={parentId}
                  parentType={parentType}
                  multiselect={false}
                  shouldUpload={true}
                  exportAsPdf={true}
                  enableShareModal={false}
                  pickerType="GoogleDrive"
                  accept={["application/vnd.google-apps.form"]}
                  updateFilesCount={() => {}}
                  onSingleContentUploaded={onUpload}
                  portalType={portalType}
                  renderComponent={
                    <Tooltip
                      dsVersion="2.0"
                      tooltip={t("common:google_drive")}
                      showArrow={true}
                      placement="bottom"
                    >
                      <IconButton
                        dsVersion="2.0"
                        variant="neutral"
                        type="outlined"
                        size="small"
                        icon={<GoogleFormBrand />}
                        tooltip={t("worksheet:extract_google_forms")}
                        testId="worksheet-extract-from-doc-google-drive-button"
                      />
                    </Tooltip>
                  }
                />
              )}

              <Tooltip
                dsVersion="2.0"
                tooltip={t("common:add_link")}
                showArrow={true}
                placement="bottom"
              >
                <IconButton
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  size="small"
                  icon={<LinkOutlined />}
                  tooltip={t("common:add_link")}
                  onClick={e => {
                    e.stopPropagation();
                    setShowLinkModal(true);
                  }}
                  testId="worksheet-media-reference-add-link"
                />
              </Tooltip>
            </div>
          </div>
        }
      />

      {showLinkModal && (
        <LinkModal
          dsVersion="2.0"
          parentId={parentId}
          onLinkAdd={item => onUpload(_.first(_.get(item, "attachments")))}
          parentType={parentType}
          onClose={() => setShowLinkModal(false)}
          shouldExtractYoutubeDetails={true}
          showDisplayText={false}
        />
      )}

      {showMediaModal && file && (
        <TheatreModeV2
          activeAttachmentIndex={0}
          mediaAttachments={{ attachments: [file] }}
          toggleTheatreMode={() => setShowMediaModal(!showMediaModal)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const parentId = _.get(ownProps, "extractionConversationId");
  const parentType = _.get(state, "worksheet.parentEntity.type", null);
  const isToddleLibraryPortal =
    _.get(state, "login.activeTab") === "TODDLE_LIBRARY";
  // Library route defaults auth to COMMUNITY (oid: "-7"); force PLANNER to use teacher's real org token.
  const portalType = isToddleLibraryPortal
    ? "PLANNER"
    : _.get(state, "login.userInfo.portalType");

  return { parentId, parentType, portalType, isToddleLibraryPortal };
};

const mapActionCreators = {
  deleteAIConversationAttachment,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  FileAndFormPicker
);
